import type { BufferGeometry} from "three";
import { BufferAttribute, Color, Euler, Matrix4, Quaternion, Vector3 } from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { withCrownNormals } from "./geometry/crown-normals.ts";

/**
 * Kit de malhas procedurais (ADR 0007): primitivas low-poly com cor por vértice e peso de vento
 * (0 na base, até 1 no topo), mescladas numa geometria só. Usado por recursos e itens.
 */
export interface Piece {
  /** Se já traz `color` por vértice, ele multiplica `color` (sombra por vértice). */
  geometry: BufferGeometry;
  color: string;
  /** Vento na base e no topo da peça (interpolado pela altura do vértice). */
  wind?: readonly [number, number];
  position?: readonly [number, number, number];
  rotation?: readonly [number, number, number];
  scale?: readonly [number, number, number];
}

const tmpColor = new Color();

function bake(p: Piece): BufferGeometry {
  const g = p.geometry.index ? p.geometry.toNonIndexed() : p.geometry.clone();
  g.deleteAttribute("normal");
  g.deleteAttribute("uv");
  const m = new Matrix4().compose(
    new Vector3(...(p.position ?? [0, 0, 0])),
    new Quaternion().setFromEuler(new Euler(...(p.rotation ?? [0, 0, 0]))),
    new Vector3(...(p.scale ?? [1, 1, 1])),
  );
  g.applyMatrix4(m);
  const pos = g.getAttribute("position");
  const shade = g.getAttribute("color");
  const n = pos.count;
  tmpColor.set(p.color);
  const colors = new Float32Array(n * 3);
  const wind = new Float32Array(n);
  g.computeBoundingBox();
  const box = g.boundingBox!;
  const [w0, w1] = p.wind ?? [0, 0];
  for (let i = 0; i < n; i++) {
    colors[i * 3] = tmpColor.r * (shade ? shade.getX(i) : 1);
    colors[i * 3 + 1] = tmpColor.g * (shade ? shade.getY(i) : 1);
    colors[i * 3 + 2] = tmpColor.b * (shade ? shade.getZ(i) : 1);
    const t = box.max.y > box.min.y ? (pos.getY(i) - box.min.y) / (box.max.y - box.min.y) : 0;
    wind[i] = w0 + (w1 - w0) * t;
  }
  g.setAttribute("color", new BufferAttribute(colors, 3));
  g.setAttribute("wind", new BufferAttribute(wind, 1));
  return g;
}

export function merge(pieces: Piece[]): BufferGeometry {
  const merged = mergeGeometries(pieces.map(bake), false);
  if (!merged) throw new Error("falha ao mesclar malha de recurso");
  merged.computeBoundingSphere();
  // Fase 14: as copas, a normal de moita.
  return withCrownNormals(merged);
}

/** Deforma vértices por hash da posição (vértices coincidentes recebem o mesmo deslocamento). */
export function lumpy(g: BufferGeometry, amount: number, seed: number): BufferGeometry {
  const out = g.index ? g.toNonIndexed() : g.clone();
  const pos = out.getAttribute("position");
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    const h = Math.sin(Math.round(x * 97) * 12.9898 + Math.round(y * 89) * 78.233 + Math.round(z * 83) * 37.719 + seed) * 43758.5453;
    const k = 1 + (h - Math.floor(h) - 0.5) * amount;
    pos.setXYZ(i, x * k, y * (0.75 + 0.25 * k), z * k);
  }
  return out;
}

